/**
 * send-limits-display — pure copy/format helpers for the per-professional email
 * send limits (SEC-06). The backend owns every NUMBER (limits, used, remaining,
 * ramp step, spread, queued, resume time); this module only renders them into
 * sentences. Nothing here computes a spread, a limit or a ramp threshold.
 *
 * Fail-closed: a helper returns `null` when its inputs are missing or would
 * read as nonsense ("0 of 0"), and the caller then renders nothing.
 *
 * Every function takes an injectable `now` so day words ("today", "tomorrow")
 * are deterministic in tests.
 */
import type { Campaign, CampaignSpreadDay } from "./api";
import type {
  BookImportJob,
  SendClass,
  SendingClassUsage,
  SendingPaused,
  SendingRamp,
  SendingUsageResponse,
} from "./sending-api";

const NUMBER_FORMAT = new Intl.NumberFormat("en-US");

export function formatCount(n: number): string {
  return NUMBER_FORMAT.format(n);
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function localTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** Local clock time of an RFC3339 instant, e.g. "5:00 PM"; null if unparsable. */
export function formatResetTime(iso: string | null | undefined): string | null {
  const d = parseInstant(iso);
  return d ? localTime(d) : null;
}

/** "after midnight" at a local midnight reset, else "at 5:00 PM". */
export function resetWhen(iso: string | null | undefined): string | null {
  const d = parseInstant(iso);
  if (!d) return null;
  if (d.getHours() === 0 && d.getMinutes() === 0) return "after midnight";
  return `at ${localTime(d)}`;
}

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * "today" / "tomorrow" / weekday name within the next week / "Nov 3" beyond.
 * Dates are compared in the viewer's local calendar.
 */
export function relativeDay(target: Date, now: Date): string {
  const diff = Math.round(
    (startOfLocalDay(target) - startOfLocalDay(now)) / 86_400_000,
  );
  if (diff <= 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff < 7) return target.toLocaleDateString(undefined, { weekday: "long" });
  return target.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ── Usage meter ──────────────────────────────────────────────────────────────

export interface UsageMeterModel {
  used: number;
  limit: number;
  /** 0..1, for the meter fill. */
  ratio: number;
  /** True at 80%+ of the limit — tint the meter. */
  nearLimit: boolean;
  atLimit: boolean;
  line: string;
}

/** The usage of one class, or null when unknown / a zero limit. */
export function classUsage(
  usage: SendingUsageResponse | null,
  cls: SendClass,
): SendingClassUsage | null {
  const c = usage?.classes?.[cls];
  if (!c || !(c.limit > 0) || c.used < 0) return null;
  return c;
}

/** "620 of 1,000 sent today · resets at 5:00 PM". Null when unknown. */
export function usageMeter(
  usage: SendingUsageResponse | null,
  cls: SendClass,
): UsageMeterModel | null {
  const c = classUsage(usage, cls);
  if (!usage || !c) return null;
  const reset = formatResetTime(usage.resets_at);
  const head = `${formatCount(c.used)} of ${formatCount(c.limit)} sent today`;
  const ratio = Math.min(1, c.used / c.limit);
  return {
    used: c.used,
    limit: c.limit,
    ratio,
    nearLimit: ratio >= 0.8,
    atLimit: c.used >= c.limit,
    line: reset ? `${head} · resets at ${reset}` : head,
  };
}

/**
 * The ramp line under the meter. Pro only (Free has no ramp). While the
 * pro's rates are not clean the limit is held and the line says so neutrally;
 * at the top step (no next step) there is nothing to say.
 */
export function rampLine(
  plan: SendingUsageResponse["plan"],
  ramp: SendingRamp | null | undefined,
): string | null {
  if (plan !== "pro" || !ramp) return null;
  if (!ramp.clean) {
    return `Your limit is holding at ${formatCount(ramp.daily_limit)} a day while bounce or complaint rates come down.`;
  }
  if (ramp.next_daily_limit == null || ramp.next_requires_sends_14d == null) {
    return null;
  }
  return `Send ${formatCount(ramp.next_requires_sends_14d)} clean emails in 14 days to unlock ${formatCount(ramp.next_daily_limit)}/day.`;
}

// ── Campaign spread / progress ───────────────────────────────────────────────

const MAX_SPREAD_DAYS_SHOWN = 3;

function utcDayNumber(y: number, m: number, d: number): number {
  return Date.UTC(y, m, d) / 86_400_000;
}

/**
 * Spread rows are UTC `YYYY-MM-DD` days (the backend budgets in UTC). Label
 * one by its UTC distance from today's UTC date: "today" / "tomorrow" /
 * weekday within a week / "Oct 25" beyond. Null if the string is malformed.
 */
export function relativeSpreadDay(dateStr: string, now: Date = new Date()): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const diff =
    utcDayNumber(y, mo, d) -
    utcDayNumber(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  if (diff <= 0) return "today";
  if (diff === 1) return "tomorrow";
  const target = new Date(Date.UTC(y, mo, d));
  if (diff < 7) return target.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });
  return target.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/** True when the first spread row is a UTC day after today (today's budget is spent). */
export function spreadStartsLater(
  spread: CampaignSpreadDay[] | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!spread || spread.length === 0) return false;
  return spread[0].date > now.toISOString().slice(0, 10);
}

/** Not "all sent right now": several days, or a single day that is not today. */
export function spreadIsDeferred(
  spread: CampaignSpreadDay[] | null | undefined,
  now: Date = new Date(),
): boolean {
  return (spread?.length ?? 0) > 1 || spreadStartsLater(spread, now);
}

/**
 * "2,500 recipients · 1,000 today, 1,500 tomorrow". `total` is the eligible
 * count from the same preview. Null when there is no spread to describe.
 */
export function spreadSummary(
  spread: CampaignSpreadDay[] | null | undefined,
  total: number,
  now: Date = new Date(),
): string | null {
  if (!spread || spread.length === 0) return null;
  const days = spread.map((s) => ({ count: s.count, label: relativeSpreadDay(s.date, now) }));
  if (days.some((d) => d.label === null)) return null;
  const noun = plural(total, "recipient", "recipients");
  const head = `${formatCount(total)} ${noun}`;
  if (days.length === 1) {
    return `${head} · all ${days[0].label}`;
  }
  const shown = days.slice(0, MAX_SPREAD_DAYS_SHOWN).map(
    (d) => `${formatCount(d.count)} ${d.label}`,
  );
  const rest = days.length - MAX_SPREAD_DAYS_SHOWN;
  const tail = rest > 0 ? `, and ${rest} more ${plural(rest, "day", "days")}` : "";
  return `${head} · ${shown.join(", ")}${tail}`;
}

/**
 * Schedule-popover wording: the spread's dates are relative to today, not to
 * the picked instant, so only the shape is stated. Null for a single send that starts today.
 */
export function scheduleSpreadNote(
  spread: CampaignSpreadDay[] | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!spread || spread.length === 0) return null;
  if (spread.length === 1) {
    return spreadStartsLater(spread, now)
      ? "Today's sending limit is used up, so this goes out on a later day."
      : null;
  }
  return `At your daily limit this goes out over ${spread.length} days, ${formatCount(spread[0].count)} on the first day.`;
}

/** "1,000 sent · 1,500 queued · resumes tomorrow 12:00 AM". Null if none queued. */
export function campaignProgressLine(
  c: Pick<Campaign, "sent_count" | "queued_count" | "resumes_at">,
  now: Date = new Date(),
): string | null {
  const queued = c.queued_count ?? 0;
  if (queued <= 0) return null;
  let line = `${formatCount(c.sent_count)} sent · ${formatCount(queued)} queued`;
  const resumes = parseInstant(c.resumes_at);
  if (resumes) {
    line += ` · resumes ${relativeDay(resumes, now)} ${localTime(resumes)}`;
  }
  return line;
}

// ── Paused banner / clean list ───────────────────────────────────────────────

export function pausedMessage(paused: SendingPaused): string {
  let reason: string;
  switch (paused.reason) {
    case "complaint_rate":
      reason = "too many recipients marked your emails as spam";
      break;
    case "bounce_rate":
      reason = "too many emails bounced";
      break;
    default:
      return "Bulk sending is paused while we review your account. 1:1 email still works.";
  }
  return `Bulk sending is paused: ${reason}. 1:1 email still works.`;
}

export function cleanListResultMessage(archived: number): string {
  const review = "We've asked our team to review your account.";
  if (archived <= 0) {
    return `No contacts with bounced or complained emails were found. ${review}`;
  }
  return `Archived ${formatCount(archived)} ${plural(archived, "contact", "contacts")} with bounced or complained emails. ${review}`;
}

// ── 429 send_limit_reached toast ─────────────────────────────────────────────

const CLASS_NOUN: Record<SendClass, string> = {
  one_to_one: "emails",
  campaign: "campaign emails",
  automation: "automated emails",
  invite: "invites",
  book_import: "invites",
};

const SEND_CLASSES = new Set<string>(Object.keys(CLASS_NOUN));

export function isSendClass(v: unknown): v is SendClass {
  return typeof v === "string" && SEND_CLASSES.has(v);
}

export interface SendLimitBody {
  class?: unknown;
  limit?: unknown;
  plan?: unknown;
  resets_at?: unknown;
  upgrade?: unknown;
}

export interface SendLimitCopy {
  title: string;
  description: string;
  /** Show the Upgrade CTA (Free plan). */
  upgrade: boolean;
}

/** Toast copy for a 429/skipped-by-limit: names the limit and the reset. */
export function sendLimitCopy(body: SendLimitBody): SendLimitCopy {
  const cls: SendClass = isSendClass(body.class) ? body.class : "one_to_one";
  const noun = CLASS_NOUN[cls];
  const limit =
    typeof body.limit === "number" && body.limit > 0 ? formatCount(body.limit) : null;
  const upgrade = body.upgrade === true;
  const when = resetWhen(typeof body.resets_at === "string" ? body.resets_at : null);
  const planName = body.plan === "free" ? " on Free" : "";
  const title = limit
    ? `You've sent today's ${limit} ${noun}${planName}.`
    : `You've reached today's ${noun} limit${planName}.`;
  let description = "Your limit resets tomorrow.";
  if (upgrade) {
    const resume = when ? `send more ${when}` : "send more tomorrow";
    description = `Upgrade to Pro for a higher daily limit, or ${resume}.`;
  } else if (when) {
    description = `Your limit resets ${when}.`;
  }
  return { title, description, upgrade };
}

// ── Book import ──────────────────────────────────────────────────────────────

/** Bucket labels for analytics — never the exact count. */
export function countBucket(n: number): string {
  if (n <= 100) return "1-100";
  if (n <= 500) return "101-500";
  if (n <= 1000) return "501-1000";
  if (n <= 2500) return "1001-2500";
  if (n <= 5000) return "2501-5000";
  return "5000+";
}

/** Pace statement before starting. Null when the daily pace is unknown. */
export function bookImportPaceLine(
  count: number,
  dailyLimit: number | null | undefined,
): string | null {
  if (count <= 0 || !dailyLimit || dailyLimit <= 0) return null;
  const people = `${formatCount(count)} ${plural(count, "contact", "contacts")}`;
  const days = Math.ceil(count / dailyLimit);
  if (days <= 1) return `${people} will be invited today.`;
  return `${people} will be invited over ${days} days, about ${formatCount(dailyLimit)} a day.`;
}

export interface BookImportProgressModel {
  title: string;
  line: string;
  /** Extra neutral sentence (paused / waiting reason), or null. */
  note: string | null;
  failed: number;
  done: boolean;
  ratio: number;
}

export function bookImportProgress(
  job: BookImportJob,
  now: Date = new Date(),
): BookImportProgressModel {
  const done = job.status === "done";
  const sent = formatCount(job.sent);
  const total = formatCount(job.total);
  let line = `${sent} of ${total} sent`;
  if (!done && job.queued > 0) line += ` · ${formatCount(job.queued)} queued`;
  const resume = parseInstant(job.resume_at);
  if (!done && job.status === "waiting" && resume) {
    line += ` · next batch ${relativeDay(resume, now)}`;
  }
  let note: string | null = null;
  if (!done && job.paused_reason === "daily_limit") {
    note = "Today's invite limit is reached. The rest continue automatically.";
  } else if (!done && job.paused_reason === "bulk_paused") {
    note = "Paused while bulk sending is paused on your account.";
  }
  return {
    title: done ? "Invited the people you imported" : "Inviting the people you imported",
    line,
    note,
    failed: job.failed.length,
    done,
    ratio: job.total > 0 ? Math.min(1, job.sent / job.total) : 0,
  };
}
